'use client';

type Callback = (data: any) => void;

export class WSChannel {
  channelName: string;
  callbacks: Map<string, Set<Callback>>;

  constructor(channelName: string) {
    this.channelName = channelName;
    this.callbacks = new Map();
  }

  bind(eventName: string, callback: Callback) {
    if (!this.callbacks.has(eventName)) {
      this.callbacks.set(eventName, new Set());
    }
    this.callbacks.get(eventName)!.add(callback);
    return this;
  }

  unbind(eventName: string, callback?: Callback) {
    if (!callback) {
      this.callbacks.delete(eventName);
    } else {
      this.callbacks.get(eventName)?.delete(callback);
    }
    return this;
  }

  emit(eventName: string, data: any) {
    const set = this.callbacks.get(eventName);
    if (set) {
      set.forEach(fn => {
        try {
          fn(data);
        } catch (e) {
          console.error(`[WS Client Error] Exception in callback for ${eventName}:`, e);
        }
      });
    }
  }
}

export interface WSAuthContext {
  userId?: number;
  teamId?: number;
  role?: string;
  canSeeAllChats?: boolean;
}

class BuiltinWSClient {
  ws: WebSocket | null = null;
  channels: Map<string, WSChannel> = new Map();
  reconnectTimer: any = null;
  pingTimer: any = null;
  isConnecting = false;
  authContext: WSAuthContext = {};

  constructor() {
    if (typeof window !== 'undefined') {
      this.connect();
      this.startPing();
    }
  }

  identify(context: WSAuthContext) {
    this.authContext = { ...this.authContext, ...context };
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify({ type: 'auth', ...this.authContext }));
      } catch (e) {}
    }
  }

  private startPing() {
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        try {
          this.ws.send(JSON.stringify({ type: 'ping' }));
        } catch (e) {}
      }
    }, 25000);
  }

  connect() {
    if (typeof window === 'undefined') return;
    if (this.ws?.readyState === WebSocket.OPEN || this.isConnecting) return;

    this.isConnecting = true;

    // Detect websocket URL:
    // If on localhost / 127.0.0.1, connect directly to port 3001
    // If on a domain (e.g. whatsappcrm-dev.digis.pk), connect via reverse proxy /ws
    let wsUrl: string = process.env.NEXT_PUBLIC_WS_URL || '';
    if (!wsUrl) {
      const hostname = window.location.hostname;
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        wsUrl = `ws://${hostname}:3001/ws`;
      } else {
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        wsUrl = `${protocol}//${window.location.host}/ws`;
      }
    }

    try {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        this.isConnecting = false;
        console.log('[Built-in WebSocket] Connected to', wsUrl);

        // Send identity if already available
        if (this.authContext.userId) {
          try {
            this.ws?.send(JSON.stringify({ type: 'auth', ...this.authContext }));
          } catch (e) {}
        }

        // Resubscribe all registered channels
        this.channels.forEach((_, channelName) => {
          this.sendSubscribe(channelName);
        });
      };

      this.ws.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (payload.type === 'pong' || payload.type === 'auth-ack') return;

          if (payload.event && payload.channel) {
            const channelObj = this.channels.get(payload.channel);
            if (channelObj) {
              channelObj.emit(payload.event, payload.data);
            }
          }
        } catch (e) {
          // ignore malformed frame
        }
      };

      this.ws.onclose = () => {
        this.isConnecting = false;
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = setTimeout(() => this.connect(), 2500);
      };

      this.ws.onerror = () => {
        this.isConnecting = false;
      };
    } catch {
      this.isConnecting = false;
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = setTimeout(() => this.connect(), 2500);
    }
  }

  private sendSubscribe(channelName: string) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify({ 
          type: 'subscribe', 
          channel: channelName,
          ...this.authContext,
        }));
      } catch (e) {}
    }
  }

  subscribe(channelName: string): WSChannel {
    if (!this.channels.has(channelName)) {
      this.channels.set(channelName, new WSChannel(channelName));
    }

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.sendSubscribe(channelName);
    } else {
      this.connect();
    }

    return this.channels.get(channelName)!;
  }

  channel(channelName: string): WSChannel | undefined {
    return this.channels.get(channelName);
  }
}

let clientInstance: BuiltinWSClient | null = null;

export function getWSClient(): BuiltinWSClient | null {
  if (typeof window === 'undefined') return null;
  if (!clientInstance) {
    clientInstance = new BuiltinWSClient();
  }
  return clientInstance;
}

// Built-in getTeamChannel for all CRM components
export function getTeamChannel(teamId: number | string): WSChannel | null {
  const client = getWSClient();
  if (!client) return null;
  return client.subscribe(`team-${teamId}`);
}

export function identifyWS(context: WSAuthContext) {
  const client = getWSClient();
  if (client) {
    client.identify(context);
  }
}

// Alias for backwards-compatibility
export const getPusherClient = getWSClient;
